import { CreqReviewState, HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap, MapLocKeys } from '../../models';
import { LocDisplayType } from '../../models/display-type';
import { SYSTEM_ENUM_ROOT, SYSTEM_ENUM_TYPE } from '../shared-data';

const dpRoot = `${SYSTEM_ENUM_ROOT}|Credential Requirement Review State|`;

const sharedLocData: Pick<LocDef, 'displayType' | 'htmlAllowed' | 'shared' | 'feature'> = {
	displayType: `${SYSTEM_ENUM_TYPE}` as LocDisplayType,
	htmlAllowed: false,
	shared: true,
	feature: HubFeature.OtherRequirement,
};

export type CReqReviewStateLocs =
	| 'CReqReviewStateNone'
	| 'CReqReviewStatePendingPostReview'
	| 'CReqReviewStatePendingPreReview'
	| 'CReqReviewStateReviewed'
	| 'CReqReviewStateUnderReview';

const creqReviewStateDefaults: { [key in CReqReviewStateLocs]: Omit<LocDef, 'portal'> } = {
	CReqReviewStateNone: {
		defaultValue: 'None',
		displayPath: `${dpRoot}${CreqReviewState.None}`,
		description: `System value for '${CreqReviewState.None}' review state.`,
		legacyName: 'OtherRequirement/System.Enum.CreqReviewState.None',
		...sharedLocData,
	},
	CReqReviewStatePendingPostReview: {
		defaultValue: 'Pending Post Review',
		displayPath: `${dpRoot}${CreqReviewState.PendingPostReview}`,
		description: `System value for '${CreqReviewState.PendingPostReview}' review state.`,
		legacyName: 'OtherRequirement/System.Enum.CreqReviewState.PendingPostReview',
		...sharedLocData,
	},
	CReqReviewStatePendingPreReview: {
		defaultValue: 'Pending Pre Review',
		displayPath: `${dpRoot}${CreqReviewState.PendingPreReview}`,
		description: `System value for '${CreqReviewState.PendingPreReview}' review state.`,
		legacyName: 'OtherRequirement/System.Enum.CreqReviewState.PendingPreReview',
		...sharedLocData,
	},
	CReqReviewStateReviewed: {
		defaultValue: 'Reviewed',
		displayPath: `${dpRoot}${CreqReviewState.Reviewed}`,
		description: `System value for '${CreqReviewState.Reviewed}' review state.`,
		legacyName: 'OtherRequirement/System.Enum.CreqReviewState.Reviewed',
		...sharedLocData,
	},
	CReqReviewStateUnderReview: {
		defaultValue: 'Audit',
		displayPath: `${dpRoot}${CreqReviewState.UnderReview}`,
		description: `System value for '${CreqReviewState.UnderReview}' review state.`,
		legacyName: 'OtherRequirement/System.Enum.CreqReviewState.UnderReview',
		...sharedLocData,
	},
};

export type CReqReviewStateLocType = LocMap<CReqReviewStateLocs>;

export const spCReqReviewStateLocs: LocDefs<CReqReviewStateLocs> = {
	CReqReviewStateNone: {
		...creqReviewStateDefaults.CReqReviewStateNone,
		portal: 'SP',
	},
	CReqReviewStatePendingPostReview: {
		...creqReviewStateDefaults.CReqReviewStatePendingPostReview,
		portal: 'SP',
	},
	CReqReviewStatePendingPreReview: {
		...creqReviewStateDefaults.CReqReviewStatePendingPreReview,
		portal: 'SP',
	},
	CReqReviewStateReviewed: {
		...creqReviewStateDefaults.CReqReviewStateReviewed,
		portal: 'SP',
	},
	CReqReviewStateUnderReview: {
		...creqReviewStateDefaults.CReqReviewStateUnderReview,
		portal: 'SP',
	},
};

export const SP_CREQ_REVIEW_STATE_ENUM_LOCS = MapLocKeys<CReqReviewStateLocs>({
	...spCReqReviewStateLocs,
});

export const spCReqReviewStateKeys: { [key in CreqReviewState]?: keyof CReqReviewStateLocType } = {
	[CreqReviewState.None]: SP_CREQ_REVIEW_STATE_ENUM_LOCS.CReqReviewStateNone,
	[CreqReviewState.PendingPostReview]:
		SP_CREQ_REVIEW_STATE_ENUM_LOCS.CReqReviewStatePendingPostReview,
	[CreqReviewState.PendingPreReview]:
		SP_CREQ_REVIEW_STATE_ENUM_LOCS.CReqReviewStatePendingPreReview,
	[CreqReviewState.Reviewed]: SP_CREQ_REVIEW_STATE_ENUM_LOCS.CReqReviewStateReviewed,
	[CreqReviewState.UnderReview]: SP_CREQ_REVIEW_STATE_ENUM_LOCS.CReqReviewStateUnderReview,
};

export const cpCReqReviewStateLocs: LocDefs<CReqReviewStateLocs> = {
	CReqReviewStateNone: {
		...creqReviewStateDefaults.CReqReviewStateNone,
		portal: 'CP',
	},
	CReqReviewStatePendingPostReview: {
		...creqReviewStateDefaults.CReqReviewStatePendingPostReview,
		portal: 'CP',
	},
	CReqReviewStatePendingPreReview: {
		...creqReviewStateDefaults.CReqReviewStatePendingPreReview,
		portal: 'CP',
	},
	CReqReviewStateReviewed: {
		...creqReviewStateDefaults.CReqReviewStateReviewed,
		portal: 'CP',
	},
	CReqReviewStateUnderReview: {
		...creqReviewStateDefaults.CReqReviewStateUnderReview,
		portal: 'CP',
	},
};

export const CP_CREQ_REVIEW_STATE_ENUM_LOCS = MapLocKeys<CReqReviewStateLocs>({
	...cpCReqReviewStateLocs,
});

export const cpCReqReviewStateKeys: { [key in CreqReviewState]?: keyof CReqReviewStateLocType } = {
	[CreqReviewState.None]: CP_CREQ_REVIEW_STATE_ENUM_LOCS.CReqReviewStateNone,
	[CreqReviewState.PendingPostReview]:
		CP_CREQ_REVIEW_STATE_ENUM_LOCS.CReqReviewStatePendingPostReview,
	[CreqReviewState.PendingPreReview]:
		CP_CREQ_REVIEW_STATE_ENUM_LOCS.CReqReviewStatePendingPreReview,
	[CreqReviewState.Reviewed]: CP_CREQ_REVIEW_STATE_ENUM_LOCS.CReqReviewStateReviewed,
	[CreqReviewState.UnderReview]: CP_CREQ_REVIEW_STATE_ENUM_LOCS.CReqReviewStateUnderReview,
};
